import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { BASE_URL, SITE_NAME, isPublicRoute, getKeywords } from '@/lib/seo';
import { SUPPORTED_LANGUAGES, localeTagFor, normalizeLanguage } from '@/lib/languageDetection';
import { setAllowedTitle } from '@/lib/lockDocumentTitle';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  canonical?: string;
  type?: 'website' | 'article' | 'product';
  noindex?: boolean;
  structuredData?: object;
}

/** Generic title used on private/admin areas so internal module names never leak. */
const GENERIC_TITLE = 'Berastays.com/App';

export const SEOHead = ({
  title,
  description,
  keywords,
  image,
  url,
  canonical,
  type = 'website',
  noindex = false,
  structuredData,
}: SEOHeadProps) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = normalizeLanguage(i18n.language) || 'pt';

  const publicPage = isPublicRoute(location.pathname);

  // Public pages expose the real, translated title. Private/admin areas stay generic.
  const finalTitle = publicPage ? title || t('seo.homeTitle') || SITE_NAME : GENERIC_TITLE;
  const finalDescription = description || t('seo.homeDesc');
  const finalKeywords = keywords || getKeywords(t).join(', ');
  const finalImage = image || `${BASE_URL}/og-image.png`;
  const finalUrl = url || `${BASE_URL}${location.pathname}`;
  const finalCanonical = canonical || finalUrl;
  const shouldIndex = !noindex && publicPage;

  useEffect(() => {
    setAllowedTitle(finalTitle);
    document.documentElement.lang = lang;

    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (meta) {
        meta.setAttribute('content', content);
      } else {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };

    // Primary meta tags
    updateMetaTag('title', finalTitle);
    updateMetaTag('description', finalDescription);
    updateMetaTag('keywords', finalKeywords);
    updateMetaTag('robots', shouldIndex ? 'index, follow' : 'noindex, nofollow');
    updateMetaTag('language', lang);

    // Open Graph
    updateMetaTag('og:title', finalTitle, true);
    updateMetaTag('og:description', finalDescription, true);
    updateMetaTag('og:image', finalImage, true);
    updateMetaTag('og:url', finalUrl, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:locale', localeTagFor(lang), true);
    document.head
      .querySelectorAll('meta[property="og:locale:alternate"]')
      .forEach((el) => el.remove());
    SUPPORTED_LANGUAGES.filter((l) => l !== lang).forEach((alt) => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:locale:alternate');
      meta.setAttribute('content', localeTagFor(alt));
      document.head.appendChild(meta);
    });

    // Twitter
    updateMetaTag('twitter:title', finalTitle);
    updateMetaTag('twitter:description', finalDescription);
    updateMetaTag('twitter:image', finalImage);

    // Canonical
    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', finalCanonical);

    // hreflang alternates (same URL, language negotiated by the app)
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    [...SUPPORTED_LANGUAGES, 'x-default'].forEach((code) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', code);
      link.setAttribute('href', finalCanonical);
      document.head.appendChild(link);
    });

    // Structured data
    if (structuredData) {
      const existingScript = document.querySelector('script[data-seo-structured]');
      if (existingScript) existingScript.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-structured', 'true');
      script.textContent = JSON.stringify({ ...structuredData, inLanguage: lang });
      document.head.appendChild(script);
    }

    return () => {
      const script = document.querySelector('script[data-seo-structured]');
      if (script) script.remove();
    };
  }, [
    finalTitle,
    finalDescription,
    finalKeywords,
    finalImage,
    finalUrl,
    finalCanonical,
    type,
    shouldIndex,
    structuredData,
    lang,
  ]);

  return null;
};

// Helper function to generate property structured data
export const generatePropertyStructuredData = (property: {
  id: string;
  title: string;
  description?: string;
  price_per_night: number;
  currency?: string;
  city: string;
  province: string;
  images?: string[];
  average_rating?: number;
  total_reviews?: number;
  bedrooms?: number;
  bathrooms?: number;
  max_guests?: number;
  amenities?: string[];
}) => ({
  "@context": "https://schema.org",
  "@type": "LodgingBusiness",
  "name": property.title,
  "description": property.description || `${property.title} — ${property.city}, ${property.province}`,
  "image": property.images?.[0] || `${BASE_URL}/og-image.png`,
  "url": `${BASE_URL}/property/${property.id}`,
  "address": {
    "@type": "PostalAddress",
    "addressLocality": property.city,
    "addressRegion": property.province,
    "addressCountry": "AO"
  },
  "priceRange": `${property.price_per_night} ${property.currency || 'Kz'}`,
  ...(property.average_rating && {
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": property.average_rating,
      "reviewCount": property.total_reviews || 0
    }
  }),
  "amenityFeature": property.amenities?.map(amenity => ({
    "@type": "LocationFeatureSpecification",
    "name": amenity
  })) || [],
  "numberOfRooms": property.bedrooms,
  "occupancy": {
    "@type": "QuantitativeValue",
    "maxValue": property.max_guests
  }
});

// Helper function to generate breadcrumb structured data
export const generateBreadcrumbStructuredData = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

// Helper function to generate FAQ structured data
export const generateFAQStructuredData = (faqs: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export default SEOHead;
